import { Loader } from '@progress/kendo-react-indicators'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import { compile } from '~/apps/ninesegsAnalyzer/routes/paths'
import { Box, type BoxProps } from '~/components/elements/Box'
import { HStack } from '~/components/elements/HStack'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { useScrollToBottom } from '~/hooks/useScrollToBottom'

import { Dialogs } from './components/Dialogs'
import { DocumentImageCard } from './components/DocumentImageCard'
import { SearchWordInput } from './components/SearchWordInput'
import { ShowAllCheckbox } from './components/ShowAllCheckbox'
import { isEnabledTransitionContext, onCardClickContext } from './contexts'
import { useBrandAccountId } from './hooks/useBrandAccountId'
import { useDocumentImagesInfiniteQuery } from './hooks/useDocumentImagesInfiniteQuery'
import { type DocumentImage } from './hooks/useFetchDocumentImages'
import { useResetOnUnmount } from './hooks/useResetOnUnmount'

export type DocumentImageInfiniteScrollListProps = {
  isArchiveShown?: boolean
  isEnabledTransition?: boolean
  onCardClick?: (documentImage: DocumentImage) => void
}

export const DocumentImageInfiniteScrollList = ({
  isArchiveShown = false,
  isEnabledTransition = false,
  onCardClick,
}: DocumentImageInfiniteScrollListProps) => {
  const { t } = useTranslation()
  const brandAccountId = useBrandAccountId()
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchedAfterMount,
    isFetching,
    isFetchingNextPage,
  } = useDocumentImagesInfiniteQuery()
  const documentImages = React.useMemo(
    () => data?.pages.flat() ?? [],
    [data?.pages],
  )

  const handleCardsContainerScroll = useScrollToBottom<
    EH<BoxProps, 'onScroll'>
  >({
    getElement: (event) => event.currentTarget,
    onScrollToBottom: () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
  })

  useResetOnUnmount()

  return (
    <isEnabledTransitionContext.Provider value={isEnabledTransition}>
      <onCardClickContext.Provider value={onCardClick}>
        <VStack spacing={4} verticalAppearance="stretch">
          <VStack spacing={1}>
            <SearchWordInput />

            <HStack align="center" justify="space-between" spacing={1}>
              <ShowAllCheckbox />

              {isArchiveShown && (
                <Link
                  to={compile('documentImageArchiveList', { brandAccountId })}
                >
                  <Typography color="light" variant="link2">
                    {t('bin_2')}
                  </Typography>
                </Link>
              )}
            </HStack>
          </VStack>

          {(isFetching || 0 < documentImages.length) && (
            <Box
              verticalAppearance="stretch-scroll"
              onScroll={handleCardsContainerScroll}
            >
              <VStack spacing={2}>
                {documentImages.map((documentImage) => (
                  <DocumentImageCard
                    key={documentImage.id}
                    documentImage={documentImage}
                  />
                ))}

                {isFetching && (
                  <VStack
                    align="center"
                    justify="center"
                    padding={8}
                    spacing={0}
                  >
                    <Loader type="pulsing" />
                  </VStack>
                )}
              </VStack>
            </Box>
          )}

          {isFetchedAfterMount && documentImages.length == 0 && (
            <VStack align="center" justify="center" padding={8} spacing={0}>
              <Typography variant="heading1">
                {t('no_specified_tablegraph_found')}
              </Typography>
            </VStack>
          )}
        </VStack>

        <Dialogs />
      </onCardClickContext.Provider>
    </isEnabledTransitionContext.Provider>
  )
}
